import { useState, useEffect, useRef, useCallback } from 'react';
import { getSpineManager, type IkTargetInfo } from '../../lib/spine-manager';
import { getPixiApp } from '../../lib/pixi-app';
import { useSceneStore } from '../../stores/sceneStore';
import type { Layer } from '@spine-workspace/shared';

const RULER_SIZE = 20; // must match SceneCanvas
const HANDLE_SIZE = 9; // half-width of diamond in px

interface IKHandleOverlayProps {
  layer: Layer;
  readOnly?: boolean;
}

export default function IKHandleOverlay({ layer, readOnly }: IKHandleOverlayProps) {
  const [targets, setTargets] = useState<IkTargetInfo[]>([]);
  const [dragging, setDragging] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const draggingRef = useRef<string | null>(null);
  const { setLayerIkPose } = useSceneStore();

  const refresh = useCallback(() => {
    const t = getSpineManager().getIkTargets(layer.id);
    if (t && t.length > 0) setTargets(t);
  }, [layer.id]);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const app = getPixiApp();
    if (!app) return;
    app.ticker.add(refresh);
    return () => { app.ticker?.remove(refresh); };
  }, [refresh]);

  // Document-level drag handlers so SVG itself stays pointer-events:none
  // (prevents it from blocking canvas clicks when not dragging a handle)
  useEffect(() => {
    function onMove(e: PointerEvent) {
      const name = draggingRef.current;
      if (!name || !svgRef.current) return;
      const rect = svgRef.current.getBoundingClientRect();
      getSpineManager().setIkTargetFromStage(layer.id, name, e.clientX - rect.left, e.clientY - rect.top);
    }

    function onUp() {
      const name = draggingRef.current;
      if (!name) return;
      draggingRef.current = null;
      setDragging(null);

      const updatedTargets = getSpineManager().getIkTargets(layer.id);
      const constraints = getSpineManager().getIkConstraints(layer.id);
      if (updatedTargets && constraints) {
        for (const t of updatedTargets) {
          const c = constraints.find((c) => c.name === t.constraintName);
          if (c) {
            setLayerIkPose(layer.id, t.constraintName, {
              localX: t.localX,
              localY: t.localY,
              mix: c.mix,
              bendDirection: c.bendDirection,
            });
          }
        }
      }
    }

    document.addEventListener('pointermove', onMove, { capture: true });
    document.addEventListener('pointerup', onUp, { capture: true });
    return () => {
      document.removeEventListener('pointermove', onMove, { capture: true });
      document.removeEventListener('pointerup', onUp, { capture: true });
    };
  }, [layer.id, setLayerIkPose]);

  if (targets.length === 0) return null;

  function onHandlePointerDown(e: React.PointerEvent<SVGPolygonElement>, name: string) {
    if (readOnly) return;
    e.stopPropagation();
    e.preventDefault(); // prevent browser native drag hijacking pointermove
    draggingRef.current = name;
    setDragging(name);
  }

  return (
    <svg
      ref={svgRef}
      className="absolute"
      style={{
        left: RULER_SIZE,
        top: RULER_SIZE,
        width: `calc(100% - ${RULER_SIZE}px)`,
        height: `calc(100% - ${RULER_SIZE}px)`,
        overflow: 'visible',
        zIndex: 31,
        // SVG itself is transparent to clicks — only the handle polygons capture events
        pointerEvents: 'none',
        cursor: dragging ? 'grabbing' : undefined,
      }}
    >
      {targets.map((t) => {
        const { stageX: cx, stageY: cy } = t;
        const isActive = dragging === t.constraintName;
        const pts = `${cx},${cy - HANDLE_SIZE} ${cx + HANDLE_SIZE},${cy} ${cx},${cy + HANDLE_SIZE} ${cx - HANDLE_SIZE},${cy}`;

        return (
          <g key={t.constraintName}>
            {/* Outer glow ring */}
            <polygon
              points={pts}
              fill="none"
              stroke={isActive ? 'rgba(167,139,250,0.6)' : 'rgba(139,92,246,0.3)'}
              strokeWidth={isActive ? 8 : 5}
              strokeLinejoin="round"
              style={{ pointerEvents: 'none' }}
            />
            {/* Diamond — the only element that captures pointer events */}
            <polygon
              data-testid={`ik-handle-${t.constraintName}`}
              points={pts}
              fill={isActive ? 'rgba(167,139,250,0.95)' : 'rgba(139,92,246,0.85)'}
              stroke="rgba(0,0,0,0.5)"
              strokeWidth={1.5}
              strokeLinejoin="round"
              style={{ cursor: readOnly ? 'default' : isActive ? 'grabbing' : 'grab', pointerEvents: readOnly ? 'none' : 'all' }}
              onPointerDown={(e) => onHandlePointerDown(e, t.constraintName)}
              onDragStart={(e) => e.preventDefault()}
            />
            {/* Label */}
            {!dragging && (
              <text
                x={cx + HANDLE_SIZE + 4}
                y={cy + 4}
                fontSize={10}
                fontFamily="monospace"
                fill={isActive ? 'rgba(167,139,250,1)' : 'rgba(139,92,246,0.9)'}
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                {t.constraintName}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}
